const TRACKING_NUMBER = /^[A-Z0-9-]{5,50}$/;
const GENERIC_ERROR = "Tracking is unavailable. Please try again shortly.";
const CONFIG_ERROR = "Tracking is not configured yet.";
const SAFE_API_ERRORS = new Set([
  "Enter a valid tracking number.",
  "Tracking is temporarily unavailable. Please try again shortly.",
  "Please wait before trying again.",
]);
const STATUS_LABELS = new Map([
  ["not_found", new Set(["Tracking not active yet"])],
  ["processed", new Set(["Order processed"])],
  ["in_transit", new Set(["In transit"])],
  ["pickup", new Set(["Ready for pickup"])],
  ["out_for_delivery", new Set(["Out for delivery"])],
  ["delivered", new Set(["Delivered"])],
  ["issue", new Set(["Delivery issue", "Tracking requires assistance"])],
  ["update", new Set(["Shipment update received"])],
]);
const EVENT_LABELS = new Set([
  "Order information received",
  "Shipment accepted by the delivery network",
  "Shipment is moving through the delivery network",
  "Shipment arrived at a processing facility",
  "Shipment is undergoing customs processing",
  "Customs processing completed",
  "Shipment is moving through the delivery network",
  "Shipment is ready for pickup",
  "Shipment is out for delivery",
  "Shipment delivered",
  "A delivery issue requires attention",
  "Shipment update received",
]);

const normalizeNumber = (value) => value.trim().toUpperCase();

const isAllowedApiUrl = (url) =>
  url.protocol === "https:" ||
  (url.protocol === "http:" &&
    (url.hostname === "localhost" || url.hostname === "127.0.0.1"));

const parseApiUrl = (value) => {
  try {
    const url = new URL(value);
    return isAllowedApiUrl(url) ? url : null;
  } catch {
    return null;
  }
};

const exactKeys = (value, keys) => {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return false;
  const actual = Object.keys(value);
  return actual.length === keys.length && actual.every((key) => keys.includes(key));
};

const canonicalTime = (value) => {
  if (typeof value !== "string") return false;
  try { return new Date(value).toISOString() === value; } catch { return false; }
};

const isTrackingResponse = (body, suffix) =>
  exactKeys(body, ["events", "status", "statusLabel", "trackingSuffix"]) &&
  STATUS_LABELS.get(body.status)?.has(body.statusLabel) === true &&
  body.trackingSuffix === suffix &&
  Array.isArray(body.events) &&
  body.events.length <= 100 &&
  body.events.every((event) =>
    exactKeys(event, ["label", "time"]) &&
    EVENT_LABELS.has(event.label) &&
    canonicalTime(event.time));

const isPendingResponse = (body, suffix) =>
  exactKeys(body, ["events", "status", "statusLabel", "trackingSuffix"]) &&
  body.status === "pending" &&
  body.statusLabel === "Tracking is being activated" &&
  Array.isArray(body.events) &&
  body.events.length === 0 &&
  body.trackingSuffix === suffix;

const publicApiError = (body) =>
  body !== null &&
  typeof body === "object" &&
  !Array.isArray(body) &&
  typeof body.error === "string" &&
  SAFE_API_ERRORS.has(body.error)
    ? body.error
    : GENERIC_ERROR;

function init(root) {
  if (root.dataset.dreameryTrackerInitialized === "true") {
    return;
  }

  const form = root.querySelector("form");
  const input = root.querySelector('[name="tracking-number"]');
  const button = root.querySelector('button[type="submit"]');
  const result = root.querySelector("[aria-live]");
  const status = root.querySelector("[data-tracking-status]");
  const events = root.querySelector("[data-tracking-events]");

  if (!form || !input || !button || !result || !status || !events) {
    return;
  }

  root.dataset.dreameryTrackerInitialized = "true";
  const apiUrl = parseApiUrl(root.dataset.apiUrl || "");
  const dateFormatter = new Intl.DateTimeFormat("en-AU", {
    dateStyle: "medium",
    timeStyle: "short",
  });
  let controller = null;
  let requestId = 0;

  const setStatus = (message, state = "idle") => {
    status.textContent = message;
    status.dataset.state = state;
  };

  const setBusy = (busy) => {
    button.disabled = busy;
    result.setAttribute("aria-busy", String(busy));
  };

  const clearEvents = () => {
    events.replaceChildren();
  };

  const renderEvent = (event) => {
    const item = root.ownerDocument.createElement("li");
    const summary = root.ownerDocument.createElement("strong");
    const time = root.ownerDocument.createElement("time");
    const date = new Date(event.time);

    summary.textContent = event.label;
    time.dateTime = date.toISOString();
    time.textContent = dateFormatter.format(date);
    item.append(summary, time);
    events.append(item);
  };

  const lookup = async (value) => {
    const number = normalizeNumber(value);
    const currentRequestId = requestId + 1;
    requestId = currentRequestId;
    controller?.abort();
    controller = null;
    input.value = number;
    clearEvents();

    if (!TRACKING_NUMBER.test(number)) {
      setBusy(false);
      setStatus("Enter a valid tracking number.", "error");
      return;
    }

    if (!apiUrl) {
      setBusy(false);
      setStatus(CONFIG_ERROR, "error");
      return;
    }

    controller = new AbortController();
    const activeController = controller;
    const requestUrl = new URL(apiUrl);
    requestUrl.searchParams.set("number", number);
    setBusy(true);
    setStatus("Looking up your delivery…", "loading");

    try {
      const response = await fetch(requestUrl.toString(), {
        signal: activeController.signal,
      });
      const body = await response.json();

      if (currentRequestId !== requestId) {
        return;
      }

      if (!response.ok && response.status !== 202) {
        setStatus(publicApiError(body), "error");
        return;
      }

      const validBody = response.status === 202
        ? isPendingResponse(body, number.slice(-4))
        : isTrackingResponse(body, number.slice(-4));
      if (!validBody) {
        setStatus(GENERIC_ERROR, "error");
        return;
      }

      setStatus(
        body.statusLabel,
        response.status === 202 ? "loading" : "success",
      );
      body.events.forEach(renderEvent);
    } catch {
      if (currentRequestId === requestId) {
        setStatus(GENERIC_ERROR, "error");
      }
    } finally {
      if (currentRequestId === requestId) {
        controller = null;
        setBusy(false);
      }
    }
  };

  form.addEventListener("submit", (event) => {
    event.preventDefault();
    lookup(input.value);
  });

  const searchParams = new URLSearchParams(root.ownerDocument.defaultView.location.search);
  const number = searchParams.get("nums");

  if (number) {
    lookup(number);
  }
}

if (typeof document !== "undefined") {
  document.querySelectorAll("[data-dreamery-tracker]").forEach(init);
  document.addEventListener("shopify:section:load", (event) => {
    if (event.target.matches?.("[data-dreamery-tracker]")) {
      init(event.target);
    }
    event.target
      .querySelectorAll?.("[data-dreamery-tracker]")
      .forEach(init);
  });
}
